import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ScheduleCalendar from "../../components/ScheduleCalendar/ScheduleCalendar";
import SaturnePage from "../../components/SaturnePage/SaturnePage";
import { getErrorMessage } from "../../services/ErrorMessage";
import UserService from "../../services/UserService";
import { useScheduleCalendar } from "../../hooks/useScheduleCalendar";
import { ScheduleSummary } from "../../services/scheduling/Scheduling.types";
import { createSchedulingServices } from "../../services/scheduling/SchedulingServiceFactory";
import styles from "./ScheduleDetailsPage.module.css";

export default function ScheduleDetailsPage() {
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const { t } = useTranslation();
  const services = useMemo(createSchedulingServices, []);
  const [schedule, setSchedule] = useState<ScheduleSummary>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const schedules = useMemo(() => (schedule ? [schedule] : []), [schedule]);
  const calendar = useScheduleCalendar(services.calendarService, schedules);

  const loadSchedule = useCallback(async () => {
    if (!UserService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);

    if (!scheduleId) {
      setSchedule(undefined);
      setError(t("scheduleDetails.missingId"));
      setLoading(false);
      return;
    }

    try {
      const loaded = await services.scheduleDirectoryService.getById(scheduleId);
      setSchedule(loaded);
      if (!loaded) {
        setError(t("scheduleDetails.notFound"));
      }
    } catch (loadError) {
      setSchedule(undefined);
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [scheduleId, services.scheduleDirectoryService, t]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  return (
    <SaturnePage loading={false} needsLogin wide>
      <div className={styles.page}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/Schedules")}
        >
          <span aria-hidden="true">←</span>
          {t("scheduleDetails.back")}
        </button>
        <ScheduleCalendar
          title={schedule?.title || t("scheduleDetails.title")}
          subtitle={
            schedule
              ? t("scheduleDetails.subtitle", {
                  type: t(`calendar.actorTypes.${schedule.actorType}`),
                })
              : t("scheduleDetails.loadingSubtitle")
          }
          schedules={schedules}
          selectedScheduleIds={calendar.selectedScheduleIds}
          events={calendar.events}
          anchorDate={calendar.anchorDate}
          view={calendar.view}
          schedulesLoading={loading}
          eventsLoading={calendar.eventsLoading}
          schedulesError={error}
          eventsError={calendar.eventsError}
          onSelectedScheduleIdsChange={calendar.setSelectedScheduleIds}
          onAnchorDateChange={calendar.setAnchorDate}
          onViewChange={calendar.setView}
          onRetrySchedules={() => void loadSchedule()}
          onRetryEvents={calendar.retryEvents}
        />
      </div>
    </SaturnePage>
  );
}
